import { useCallback, useEffect, useRef, useState } from 'react';
import {
  connectFlexCredentials,
  deleteFlexConnection,
  getFlexStatus,
  syncFlexNow,
  testFlexConnection,
} from '../api/ibkrFlex';

// Flat status contract frozen by TRA-334 (backend repo). connection_status
// covers credential/test state (not_connected, untested, connected, error);
// sync_status covers the last/current Flex sync run independently.
const NOT_CONNECTED_STATUS = {
  connected: false,
  credentials_stored: false,
  connection_status: 'not_connected',
  sync_status: null,
  account_id: null,
  token: null,
  query_id: null,
  label: null,
  flex_version: null,
  last_tested_at: null,
  last_test_error_code: null,
  last_test_message: null,
  last_sync_started_at: null,
  last_sync_finished_at: null,
  last_task_id: null,
  last_import_version: null,
  last_rows_staged: null,
  last_sync_error_code: null,
  last_sync_message: null,
};

// sync_status values that mean a sync is actively in flight — poll while any
// of these hold so Sync Now doesn't leave the UI stuck on "queued".
const ACTIVE_SYNC_STATES = ['queued', 'running', 'processing'];
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const normalizeStatus = (data) => {
  if (!data || typeof data !== 'object') return NOT_CONNECTED_STATUS;
  return { ...NOT_CONNECTED_STATUS, ...data };
};

export const extractFlexErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  if (!err) return fallback;
  const data = err.response?.data;
  const msg = data?.message || data?.detail || data?.error;
  if (msg) return String(msg);
  if (err.message) return err.message;
  return fallback;
};

export default function useIbkrFlexConnection() {
  const [status, setStatus] = useState(NOT_CONNECTED_STATUS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'connect' | 'test' | 'sync' | 'delete'

  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    },
    []
  );

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFlexStatus();
      if (!mountedRef.current) return data;
      setStatus(normalizeStatus(data));
      return data;
    } catch (err) {
      if (!mountedRef.current) return null;
      setError(err);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const runAction = useCallback(async (kind, fn) => {
    setPendingAction(kind);
    setError(null);
    try {
      const data = await fn();
      if (mountedRef.current) setStatus(normalizeStatus(data));
      return { ok: true, data };
    } catch (err) {
      if (mountedRef.current) setError(err);
      return { ok: false, error: err };
    } finally {
      if (mountedRef.current) setPendingAction(null);
    }
  }, []);

  const connect = useCallback(
    (credentials) => runAction('connect', () => connectFlexCredentials(credentials)),
    [runAction]
  );
  const test = useCallback(() => runAction('test', () => testFlexConnection()), [runAction]);
  const sync = useCallback(() => runAction('sync', () => syncFlexNow()), [runAction]);
  const disconnect = useCallback(() => runAction('delete', () => deleteFlexConnection()), [runAction]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // While a sync is actively running, keep polling /status/ so Sync Now
  // reflects queued -> running -> processing -> succeeded/failed without
  // requiring the user to reopen the wizard.
  useEffect(() => {
    const isActive = ACTIVE_SYNC_STATES.includes(status.sync_status);
    if (!isActive) {
      pollDeadlineRef.current = null;
      return undefined;
    }
    if (!pollDeadlineRef.current) {
      pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    }
    if (Date.now() > pollDeadlineRef.current) {
      return undefined;
    }
    pollTimerRef.current = setTimeout(() => {
      refreshStatus();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [status.sync_status, refreshStatus]);

  return {
    status,
    loading,
    error,
    pendingAction,
    refreshStatus,
    connect,
    test,
    sync,
    disconnect,
  };
}
